import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Download, ImageIcon, Instagram, RefreshCw, Share2, Check, Film, Play } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;

// Branded fallback thumbnail shown before a video's first frame loads (and if it fails),
// so a video tile never shows an ugly black square on slow/flaky connections.
const VIDEO_POSTER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23131826'/%3E%3Cstop offset='1' stop-color='%230a0e1a'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='400' fill='url(%23g)'/%3E%3Ccircle cx='200' cy='200' r='52' fill='%23a855f7'/%3E%3Cpolygon points='186,172 186,228 232,200' fill='white'/%3E%3C/svg%3E";

// Grid thumbnail for a video: lazily (only once scrolled into view) loads the video and
// captures its actual first frame via a hidden canvas so each video shows its own preview.
// Until in view — or if the frame can't be grabbed — it shows the branded poster.
function VideoThumb({ src, className }) {
  const [thumb, setThumb] = useState(null);
  const [inView, setInView] = useState(false);
  const wrapRef = useRef(null);
  const vidRef = useRef(null);

  useEffect(() => { setThumb(null); }, [src]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "250px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  const grab = () => {
    const v = vidRef.current;
    if (!v || !v.videoWidth) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      setThumb(canvas.toDataURL("image/jpeg", 0.7));
    } catch {
      /* tainted canvas or decode issue → keep showing the poster */
    }
  };

  return (
    <div ref={wrapRef} className={className}>
      {thumb ? (
        <img src={thumb} alt="" className="h-full w-full object-cover" />
      ) : inView ? (
        <video
          ref={vidRef}
          src={src}
          poster={VIDEO_POSTER}
          muted
          playsInline
          preload="auto"
          onLoadedData={grab}
          onCanPlay={grab}
          className="h-full w-full object-cover"
        />
      ) : (
        <img src={VIDEO_POSTER} alt="" className="h-full w-full object-cover" />
      )}
    </div>
  );
}

export default function ReshareKit({ referralCode, referralLink, ambassadorName, onPostedToInstagram }) {
  const [items, setItems] = useState([]);
  const [customPlaceholders, setCustomPlaceholders] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [captionIdx, setCaptionIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [brokenIds, setBrokenIds] = useState(() => new Set());

  useEffect(() => {
    api.get("/ambassador/resources").then((r) => setItems(r.data.items || []));
    api.get("/site-settings").then((r) => setCustomPlaceholders(r.data.caption_placeholders || [])).catch(() => {});
  }, []);

  // Reshare-able MEDIA — content-library + approved community-original images AND videos.
  // Brand kit is excluded (it's for CREATING new content, not resharing).
  const isVideoFile = (f) =>
    (f.content_type || "").startsWith("video/") || /\.(mp4|mov|webm|m4v)$/i.test(f.file_path || "");
  const images = useMemo(
    () =>
      items
        .filter((f) => {
          if (!f.file_path) return false;
          if (f.category === "brand_kit") return false;
          const vid = isVideoFile(f);
          const img =
            (f.content_type || "").startsWith("image/") ||
            ["image", "logo", "banner", "template"].includes(f.file_type);
          return vid || img;
        })
        .map((f) => ({ ...f, _isVideo: isVideoFile(f) })),
    [items]
  );

  const captionTemplates = useMemo(() => items.filter((i) => i.file_type === "caption"), [items]);

  const shareableImages = useMemo(() => images.filter((i) => !brokenIds.has(i.id)), [images, brokenIds]);
  const markBroken = (id) => setBrokenIds((prev) => new Set(prev).add(id));

  const buildCaption = (t) => {
    const tokens = {
      REF_LINK: referralLink || "",
      LINK: referralLink || "",
      REF: referralCode || "",
      CODE: referralCode || "",
      NAME: ambassadorName || "",
    };
    (customPlaceholders || []).forEach((p) => { if (p.key) tokens[p.key.toUpperCase()] = p.value || ""; });
    let out = t || "";
    Object.entries(tokens).forEach(([k, v]) => {
      const re = new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}|\\{\\s*${k}\\s*\\}|\\[\\s*${k}\\s*\\]`, "gi");
      out = out.replace(re, v);
    });
    return out;
  };

  // Fallback caption when the cohort has no caption templates yet.
  const fallbackCaption =
    `Building in public with @noobsync 🚀\n\nJoin me — sign up with my link and let's grow together.\n${referralLink || ""}\n\n#NoobSync #BuildInPublic`;

  const captions = captionTemplates.length
    ? captionTemplates.map((c) => ({ name: c.name, text: buildCaption(c.caption_text || "") }))
    : [{ name: "Starter caption", text: fallbackCaption }];

  const safeIdx = captionIdx % captions.length;
  const activeCaption = captions[safeIdx];
  const selectedImage = shareableImages.find((i) => i.id === selectedImageId) || shareableImages[0] || null;

  useEffect(() => {
    if (shareableImages.length && !shareableImages.some((i) => i.id === selectedImageId)) {
      setSelectedImageId(shareableImages[0].id);
    }
  }, [shareableImages, selectedImageId]);

  const copyCaption = () => {
    navigator.clipboard.writeText(activeCaption.text);
    setCopied(true);
    toast.success("Caption copied — paste it into Instagram");
    setTimeout(() => setCopied(false), 2000);
  };

  const shuffle = () => setCaptionIdx((i) => (i + 1) % captions.length);

  return (
    <div className="space-y-6" data-testid="reshare-kit">
      <div className="rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#A855F7]/10 via-zinc-950 to-zinc-950 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-[#A855F7]/15 p-2.5 text-[#A855F7]"><Share2 className="h-5 w-5" /></div>
          <div>
            <h3 className="font-display text-xl font-bold">One-click reshare kit</h3>
            <p className="mt-1 text-sm text-zinc-400">
              Pick an image or video, grab a ready-made caption with <span className="text-[#A855F7]">your referral link</span> baked in, and post to Instagram in seconds.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-zinc-400">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1"><span className="font-bold text-[#A855F7]">1</span> Download image</span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1"><span className="font-bold text-[#A855F7]">2</span> Copy caption</span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1"><span className="font-bold text-[#A855F7]">3</span> Open Instagram &amp; post</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Image picker */}
        <div className="lg:col-span-3 rounded-2xl border border-zinc-900 bg-zinc-950 p-5">
          <div className="flex items-center justify-between">
            <h4 className="font-display font-bold">Choose an image or video</h4>
            <span className="text-xs text-zinc-500">{shareableImages.length} available</span>
          </div>
          {shareableImages.length === 0 ? (
            <p className="mt-5 text-sm text-zinc-500">No shareable content yet — approved community posts and content-library images/videos will show up here. You can still copy a caption below.</p>
          ) : (
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="reshare-image-grid">
              {shareableImages.map((img) => {
                const active = selectedImage?.id === img.id;
                return (
                  <button
                    key={img.id}
                    type="button"
                    data-testid={`reshare-image-${img.id}`}
                    onClick={() => setSelectedImageId(img.id)}
                    className={`group relative aspect-square overflow-hidden rounded-lg border-2 bg-zinc-900 transition ${active ? "border-[#A855F7] ring-2 ring-[#A855F7]/30" : "border-zinc-800 hover:border-zinc-600"}`}
                  >
                    {img._isVideo ? (
                      <>
                        <VideoThumb src={fileUrl(img.file_path)} className="h-full w-full bg-black object-cover" />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="rounded-full bg-black/60 p-1.5 text-white"><Play className="h-4 w-4" /></span>
                        </span>
                        <span className="absolute left-1 top-1 rounded bg-black/70 px-1 py-0.5 text-[9px] font-bold text-white"><Film className="inline h-2.5 w-2.5" /> video</span>
                      </>
                    ) : (
                      <img src={fileUrl(img.file_path)} alt={img.name} onError={() => markBroken(img.id)} className="h-full w-full object-cover" />
                    )}
                    {active && (
                      <span className="absolute right-1 top-1 rounded-full bg-[#A855F7] p-0.5 text-white"><Check className="h-3 w-3" /></span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Preview + actions */}
        <div className="lg:col-span-2 rounded-2xl border border-zinc-900 bg-zinc-950 p-5">
          <h4 className="font-display font-bold">Post preview</h4>
          <div className="mt-4 overflow-hidden rounded-xl border border-zinc-800 bg-black" data-testid="reshare-preview">
            {selectedImage ? (
              selectedImage._isVideo ? (
                <video src={fileUrl(selectedImage.file_path)} poster={VIDEO_POSTER} controls playsInline className="aspect-square w-full bg-black object-contain" />
              ) : (
                <img src={fileUrl(selectedImage.file_path)} alt="preview" onError={() => markBroken(selectedImage.id)} className="aspect-square w-full object-cover" />
              )
            ) : (
              <div className="flex aspect-square w-full items-center justify-center text-zinc-600"><ImageIcon className="h-8 w-8" /></div>
            )}
            <div className="p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{activeCaption.name}</span>
                {captions.length > 1 && (
                  <button onClick={shuffle} data-testid="reshare-shuffle" className="inline-flex items-center gap-1 text-[11px] text-[#A855F7] hover:underline">
                    <RefreshCw className="h-3 w-3" /> Another caption
                  </button>
                )}
              </div>
              <p className="max-h-40 overflow-y-auto whitespace-pre-wrap text-sm text-zinc-300" data-testid="reshare-caption-text">{activeCaption.text}</p>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <button
              onClick={copyCaption}
              data-testid="reshare-copy-caption"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#A855F7] px-4 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5"
            >
              {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy caption</>}
            </button>
            {selectedImage && (
              <a
                href={fileUrl(selectedImage.file_path)}
                download={selectedImage.original_filename || selectedImage.name}
                data-testid="reshare-download-image"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-zinc-700 px-4 py-2.5 text-sm font-semibold text-zinc-200 transition hover:border-zinc-500"
              >
                <Download className="h-4 w-4" /> {selectedImage._isVideo ? "Download video" : "Download image"}
              </a>
            )}
            <a
              href="https://www.instagram.com/"
              target="_blank"
              rel="noreferrer"
              data-testid="reshare-open-instagram"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-zinc-800 px-4 py-2.5 text-sm text-zinc-400 transition hover:border-zinc-600 hover:text-zinc-200"
            >
              <Instagram className="h-4 w-4" /> Open Instagram
            </a>
            <button
              onClick={() => { toast.success("Great! Paste your live post link to claim points."); onPostedToInstagram?.(); }}
              data-testid="reshare-i-posted"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-300 transition hover:bg-emerald-500/20"
            >
              <Check className="h-4 w-4" /> I posted this — claim points →
            </button>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
            Tip: after posting, tap <span className="text-emerald-300">"I posted this"</span> to jump to the Submit tab — points are awarded once you paste your live post URL and an admin approves it.
          </p>
        </div>
      </div>
    </div>
  );
}
